import Auth from '@aws-amplify/auth';

export type SessionFailureKind = 'terminal' | 'recoverable';

export class AuthSessionError extends Error {
  kind: SessionFailureKind;

  cause?: unknown;

  constructor(kind: SessionFailureKind, message: string, cause?: unknown) {
    super(message);
    this.name = 'AuthSessionError';
    this.kind = kind;
    this.cause = cause;
  }
}

export type AuthSession = Awaited<ReturnType<typeof Auth.currentSession>>;

type CognitoAttributes = Record<string, string | boolean | undefined>;

type RefreshableCognitoUser = {
  attributes?: CognitoAttributes;
  getUsername?: () => string;
  refreshSession: (
    refreshToken: ReturnType<AuthSession['getRefreshToken']>,
    callback: (error: Error | null, session: AuthSession | null) => void
  ) => void;
  username?: string;
};

export type AuthenticatedSession = {
  attributes: CognitoAttributes;
  permissions: string[];
  session: AuthSession;
  username: string;
};

const TERMINAL_ERROR_CODES = new Set([
  'NotAuthorizedException',
  'PasswordResetRequiredException',
  'UserNotConfirmedException',
  'UserNotFoundException',
]);

const TERMINAL_ERROR_MESSAGES = [
  'access token has been revoked',
  'cannot retrieve a new session. please authenticate',
  'local storage is missing an id token',
  'no current user',
  'no user session',
  'password reset required',
  'refresh token has been revoked',
  'refresh token has expired',
  'the user is not authenticated',
  'user is disabled',
  'user does not exist',
  'user is not authenticated',
  'username is null. cannot retrieve a new session',
];

let refreshSessionPromise: Promise<AuthSession> | null = null;

const getErrorCode = (error: unknown) => {
  if (typeof error !== 'object' || error === null || !('code' in error)) {
    return '';
  }

  return String(error.code);
};

const getErrorMessage = (error: unknown) => {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String(error.message);
  }

  return String(error);
};

export const classifySessionError = (error: unknown): AuthSessionError => {
  if (error instanceof AuthSessionError) {
    return error;
  }

  const code = getErrorCode(error);
  const message = getErrorMessage(error);
  const normalizedMessage = message.toLocaleLowerCase();
  const isTerminal =
    TERMINAL_ERROR_CODES.has(code) ||
    TERMINAL_ERROR_MESSAGES.some((terminalMessage) => normalizedMessage.includes(terminalMessage));

  return new AuthSessionError(isTerminal ? 'terminal' : 'recoverable', message, error);
};

export const getCurrentSession = async (): Promise<AuthSession> => {
  try {
    return await Auth.currentSession();
  } catch (error) {
    throw classifySessionError(error);
  }
};

export const getAuthenticatedSession = async (): Promise<AuthenticatedSession> => {
  try {
    const session = await getCurrentSession();
    const user = (await Auth.currentAuthenticatedUser({
      bypassCache: true,
    })) as RefreshableCognitoUser;
    const username = user?.username ?? user?.getUsername?.();
    const attributes = user?.attributes;

    if (!username || !attributes) {
      throw new AuthSessionError(
        'recoverable',
        'Authenticated user information is temporarily unavailable.'
      );
    }

    const idTokenPayload = session.getIdToken().decodePayload();
    const groups = idTokenPayload['cognito:groups'];
    const permissions = Array.isArray(groups)
      ? groups.filter((permission): permission is string => typeof permission === 'string')
      : [];

    return {
      attributes,
      permissions,
      session,
      username,
    };
  } catch (error) {
    throw classifySessionError(error);
  }
};

const refreshCurrentSession = async (): Promise<AuthSession> => {
  try {
    const currentSession = await getCurrentSession();
    const refreshToken = currentSession.getRefreshToken();

    if (!refreshToken?.getToken()) {
      throw new AuthSessionError('terminal', 'The current session has no refresh token.');
    }

    const user = (await Auth.currentAuthenticatedUser()) as RefreshableCognitoUser;

    if (!user?.refreshSession) {
      throw new AuthSessionError('terminal', 'The current user cannot refresh the session.');
    }

    return await new Promise<AuthSession>((resolve, reject) => {
      user.refreshSession(refreshToken, (error, session) => {
        if (error) {
          reject(error);
          return;
        }

        if (!session) {
          reject(
            new AuthSessionError('recoverable', 'Cognito returned an empty refreshed session.')
          );
          return;
        }

        resolve(session);
      });
    });
  } catch (error) {
    throw classifySessionError(error);
  }
};

export const forceRefreshSession = async (): Promise<AuthSession> => {
  if (!refreshSessionPromise) {
    refreshSessionPromise = refreshCurrentSession().finally(() => {
      refreshSessionPromise = null;
    });
  }

  return refreshSessionPromise;
};
